// src/components/common/QuantityStepper.jsx
import React from 'react';
import styled from '@emotion/styled';
import { Plus, Minus } from 'lucide-react';

// Size variant dimensions
const sizeVariants = {
  small: {
    buttonSize: '32px',
    iconSize: 16,
    padding: '0 12px',
    minWidth: '24px',
    fontMixin: 'textP2',
  },
  medium: {
    buttonSize: '40px',
    iconSize: 18,
    padding: '0 16px',
    minWidth: '32px',
    fontMixin: 'textP2',
  },
  large: {
    buttonSize: '48px',
    iconSize: 20,
    padding: '0 20px',
    minWidth: '40px',
    fontMixin: 'textP1',
  },
};

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  overflow: hidden;
`;

const StepperButton = styled.button`
  background: ${({ theme }) => theme.colors.backgroundLight || '#f5f5f5'};
  border: none;
  width: ${({ variant }) => sizeVariants[variant].buttonSize};
  height: ${({ variant }) => sizeVariants[variant].buttonSize};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Accessibility improvement for keyboard navigation */
  &:focus {
    position: relative;
    z-index: 1;
  }
`;

const Value = styled.span`
  padding: ${({ variant }) => sizeVariants[variant].padding};
  min-width: ${({ variant }) => sizeVariants[variant].minWidth};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme, variant }) => theme.mixins[sizeVariants[variant].fontMixin]};
  font-weight: 500;
`;

/**
 * A reusable quantity stepper component with increment and decrement functionality
 *
 * @param {Object} props
 * @param {number} props.value - Current value
 * @param {function} props.onIncrement - Function to call when increment button is clicked
 * @param {function} props.onDecrement - Function to call when decrement button is clicked
 * @param {number} [props.min=0] - Minimum allowed value
 * @param {number} [props.max=Infinity] - Maximum allowed value
 * @param {string} [props.itemName='item'] - Name of the item for accessibility labels
 * @param {('small'|'medium'|'large')} [props.variant='medium'] - Size variant of the stepper
 */
const QuantityStepper = ({
  value,
  onIncrement,
  onDecrement,
  min = 0,
  max = Infinity,
  itemName = 'item',
  variant = 'medium',
  ...props
}) => {
  const isDecrementDisabled = value <= min;
  const isIncrementDisabled = value >= max;
  const iconSize = sizeVariants[variant].iconSize;

  return (
    <StepperContainer {...props}>
      <StepperButton
        onClick={onDecrement}
        disabled={isDecrementDisabled}
        aria-label={`Decrease quantity of ${itemName}`}
        type="button"
        variant={variant}
      >
        <Minus size={iconSize} />
      </StepperButton>

      <Value
        aria-live="polite"
        aria-label={`Quantity: ${value}`}
        variant={variant}
      >
        {value}
      </Value>

      <StepperButton
        onClick={onIncrement}
        disabled={isIncrementDisabled}
        aria-label={`Increase quantity of ${itemName}`}
        type="button"
        variant={variant}
      >
        <Plus size={iconSize} />
      </StepperButton>
    </StepperContainer>
  );
};

export default QuantityStepper;
